import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const ProfessionalEmailVerification = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token');

  const [status, setStatus] = useState('verifying'); // verifying | success | expired | invalid | already_verified
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    verifyToken(token);
  }, [token]);

  const verifyToken = async (t) => {
    try {
      const { data, error } = await supabase?.rpc('verify_professional_email', { p_token: t });
      if (error) {
        console.error('Verification RPC error:', error?.message);
        setStatus('invalid');
        return;
      }
      if (data?.success) {
        if (data?.already_verified) {
          setStatus('already_verified');
        } else {
          setEmail(data?.email || '');
          setStatus('success');
        }
      } else {
        if (data?.error === 'token_expired') {
          setStatus('expired');
        } else {
          setStatus('invalid');
        }
      }
    } catch (err) {
      console.error('verifyToken error:', err?.message);
      setStatus('invalid');
    }
  };

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
          <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth">
            Vérification en cours…
          </h1>
          <p className="text-ios-body font-system font-body-normal text-text-secondary">
            Validation de votre lien de confirmation.
          </p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={44} className="text-green-600" />
          </div>
          <div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Email confirmé !
            </h1>
            <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
              Votre adresse email{email ? ` (${email})` : ''} a été vérifiée avec succès.
              Vous pouvez maintenant compléter votre candidature.
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-green-700 flex-shrink-0 mt-0.5" />
              <p className="text-ios-caption1 font-system font-body-normal text-green-700 leading-relaxed">
                Votre dossier sera examiné par notre équipe sous 48–72h après soumission complète.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/professional-signup"
              className="w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold text-center transition-all duration-200 hover:bg-brand-earth/90"
            >
              Continuer mon inscription
            </Link>
            <Link
              to="/homepage"
              className="w-full py-3 px-6 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium text-center transition-all duration-200 hover:bg-surface/80"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      );
    }

    if (status === 'already_verified') {
      return (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Icon name="ShieldCheck" size={44} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Déjà vérifié
            </h1>
            <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
              Votre email a déjà été confirmé. Vous pouvez continuer votre inscription.
            </p>
          </div>
          <Link
            to="/professional-signup"
            className="block w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold text-center transition-all duration-200 hover:bg-brand-earth/90"
          >
            Continuer mon inscription
          </Link>
        </div>
      );
    }

    if (status === 'expired') {
      return (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Clock" size={44} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
              Lien expiré
            </h1>
            <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
              Ce lien de confirmation a expiré (validité 24h). Veuillez recommencer votre inscription pour recevoir un nouveau lien.
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-ios-caption1 font-system font-body-normal text-amber-700 leading-relaxed">
                Les liens de vérification sont valables 24 heures. Créez un nouveau compte pour recevoir un nouveau lien.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/professional-signup"
              className="w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold text-center transition-all duration-200 hover:bg-brand-earth/90"
            >
              Recommencer l'inscription
            </Link>
            <Link
              to="/homepage"
              className="w-full py-3 px-6 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium text-center transition-all duration-200 hover:bg-surface/80"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      );
    }

    // invalid
    return (
      <div className="text-center space-y-6">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <Icon name="XCircle" size={44} className="text-red-500" />
        </div>
        <div>
          <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">
            Lien invalide
          </h1>
          <p className="text-ios-body font-system font-body-normal text-text-secondary leading-relaxed">
            Ce lien de vérification est invalide ou a déjà été utilisé.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Link
            to="/professional-signup"
            className="w-full py-3 px-6 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold text-center transition-all duration-200 hover:bg-brand-earth/90"
          >
            Recommencer l'inscription
          </Link>
          <Link
            to="/homepage"
            className="w-full py-3 px-6 bg-surface border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium text-center transition-all duration-200 hover:bg-surface/80"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Vérification Email — Kapital States</title>
        <meta name="description" content="Confirmez votre adresse email pour finaliser votre inscription en tant que prestataire." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-brand-gold/5 flex items-center justify-center px-4">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-border/50 px-6 py-4 flex items-center">
          <Link to="/homepage" className="flex items-center space-x-2.5 group">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center">
              <Icon name="Building2" size={18} color="white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-display-semibold text-ios-callout text-brand-earth tracking-tight">
              Kapital States
            </span>
          </Link>
        </div>

        <div className="max-w-md w-full mt-16">
          <div className="bg-white rounded-2xl border border-border shadow-ios-light p-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfessionalEmailVerification;
